import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Shield, FileText, Edit, Trash2, Loader2, Check, AlertCircle, Upload, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Alert, AlertDescription } from '../ui/alert';
import { supabase } from '../../lib/supabaseClient';
import SocialLinksManager from '../recruiter/SocialLinksManager';
import SkillsInput from '../ui/skills-input';
import CVSkillConfirmation from './CVSkillConfirmation';
import { scanCvFileForSkills, saveConfirmedSkills } from '../../utils/cvSkillExtraction';

const CLEARANCE_LEVELS = ['None', 'Baseline', 'NV1', 'NV2', 'TSPV'];
const SKILL_LEVELS = ['Beginner', 'Intermediate', 'Advanced', 'Expert'];
const FILE_TYPES = ['cv', 'cover_letter', 'portfolio', 'certification', 'other'];
const FILE_TYPE_LABELS = {
  cv: 'Resume',
  cover_letter: 'Cover Letter',
  portfolio: 'Portfolio',
  certification: 'Certification',
  other: 'Other',
};
const AUSTRALIAN_STATES = ['ACT', 'NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT'];

const MyDetails = () => {
  const [loading, setLoading] = useState(true);
  const [candidateId, setCandidateId] = useState(null);
  const [candidate, setCandidate] = useState(null);
  const [skills, setSkills] = useState([]);
  const [files, setFiles] = useState([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [error, setError] = useState(null);
  const [deletingFileId, setDeletingFileId] = useState(null);

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Request deletion of this file? It will be reviewed and removed by a recruiter.')) {
      return;
    }

    try {
      setDeletingFileId(fileId);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const API_URL = process.env.REACT_APP_API_URL || 'https://appsytec-backend.vercel.app/api';
      const response = await fetch(`${API_URL}/admin/candidates/files/${fileId}/request-deletion`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Failed to request deletion');

      // Hide from candidate view immediately
      setFiles(files.filter(f => f.id !== fileId));
    } catch (err) {
      console.error('Error requesting file deletion:', err);
      alert('Failed to request file deletion: ' + err.message);
    } finally {
      setDeletingFileId(null);
    }
  };

  useEffect(() => {
    fetchCandidateData();
  }, []);

  const fetchCandidateData = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        setError('Not authenticated');
        return;
      }

      const { data: candidateData, error: fetchError } = await supabase
        .from('candidates')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (fetchError) throw fetchError;

      setCandidate(candidateData);
      setCandidateId(candidateData.id);

      // Fetch related data
      await Promise.all([
        fetchSkills(candidateData.id),
        fetchFiles(candidateData.id)
      ]);
    } catch (err) {
      console.error('Error fetching candidate data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchSkills = async (candidateId) => {
    const { data, error } = await supabase
      .from('candidate_skills')
      .select('*, skill:skills_master(name, category)')
      .eq('candidate_id', candidateId);

    if (error) {
      console.error('Error fetching skills:', error);
      return;
    }

    setSkills(data || []);
  };

  const fetchFiles = async (candidateId) => {
    const { data, error } = await supabase
      .from('candidate_files')
      .select('*')
      .eq('candidate_id', candidateId)
      .eq('status', 'active') // hide files already marked for deletion
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching files:', error);
      return;
    }

    setFiles(data || []);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !candidate) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive">Error loading details: {error || 'Not found'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            {candidate.first_name} {candidate.last_name}
          </h1>
          <p className="text-muted-foreground mt-1">{candidate.email}</p>
        </div>
        <Button onClick={() => setEditModalOpen(true)}>
          <Edit className="h-4 w-4 mr-2" />
          Edit Details
        </Button>
      </div>

      {/* Contact Information */}
      <Card>
        <CardHeader>
          <CardTitle>Contact Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start space-x-3">
              <Mail className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Email</p>
                <p className="text-foreground">{candidate.email}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Phone className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Phone</p>
                <p className="text-foreground">{candidate.phone || 'Not provided'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Location</p>
                <p className="text-foreground">
                  {candidate.location_city && candidate.location_state
                    ? `${candidate.location_city}, ${candidate.location_state}, ${candidate.location_country || 'Australia'}`
                    : 'Not provided'}
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Shield className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Security Clearance</p>
                <Badge className="mt-1 bg-blue-100 text-blue-800">
                  {candidate.clearance_level || 'None'}
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Employment Details */}
      <Card>
        <CardHeader>
          <CardTitle>Employment Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Citizenship Status</p>
              <p className="text-foreground mt-1">{candidate.citizenship_status || 'Not specified'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Notice Period</p>
              <p className="text-foreground mt-1">{candidate.notice_period || 'Not specified'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Payroll Type</p>
              <p className="text-foreground mt-1">{candidate.payroll_type || 'Not specified'}</p>
            </div>
            {candidate.payroll_type === 'PTY LTD' && candidate.pty_ltd_abn && (
              <div>
                <p className="text-sm font-medium text-muted-foreground">PTY LTD ABN</p>
                <p className="text-foreground mt-1">{candidate.pty_ltd_abn}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Skills */}
      <Card>
        <CardHeader>
          <CardTitle>Skills</CardTitle>
          <CardDescription>{skills.length} skill{skills.length !== 1 ? 's' : ''} listed</CardDescription>
        </CardHeader>
        <CardContent>
          {skills.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {skills.map((candidateSkill) => (
                <Badge key={candidateSkill.id} variant="secondary">
                  {candidateSkill.skill?.name}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground">No skills listed</p>
          )}
        </CardContent>
      </Card>

      {/* Files & Documents */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center">
              <FileText className="h-5 w-5 mr-2" />
              Files & Documents
            </CardTitle>
            <CardDescription>{files.length} file{files.length !== 1 ? 's' : ''} uploaded</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={() => setEditModalOpen(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Upload
          </Button>
        </CardHeader>
        <CardContent>
          {files.length > 0 ? (
            <div className="space-y-3">
              {files.map((file) => (
                <div key={file.id} className="flex items-center justify-between p-3 bg-accent rounded-lg">
                  <div>
                    <p className="font-medium text-foreground">{file.file_name}</p>
                    <p className="text-sm text-muted-foreground">
                      {FILE_TYPE_LABELS[file.file_type] || file.file_type} • {file.file_size ? `${(file.file_size / 1024).toFixed(1)} KB` : 'Unknown size'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(file.file_url, '_blank')}
                    >
                      View
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteFile(file.id)}
                      disabled={deletingFileId === file.id}
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      {deletingFileId === file.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground">No files uploaded</p>
          )}
        </CardContent>
      </Card>

      {/* Social Links */}
      {candidateId && <SocialLinksManager candidateId={candidateId} isAdmin={false} showAddForm={false} />}
      {candidateId && (
        <div className="flex justify-end -mt-2">
          <Button variant="link" size="sm" className="text-xs text-muted-foreground" onClick={() => setEditModalOpen(true)}>
            <ExternalLink className="h-3 w-3 mr-1" />
            Add or edit links via Edit Details
          </Button>
        </div>
      )}

      {/* Edit Modal */}
      <EditDetailsModal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        candidate={candidate}
        candidateId={candidateId}
        existingSkills={skills}
        existingFiles={files}
        onSuccess={() => {
          fetchCandidateData();
          setEditModalOpen(false);
        }}
      />
    </div>
  );
};

// Edit Details Modal Component
const EditDetailsModal = ({ open, onOpenChange, candidate, candidateId, existingSkills, existingFiles, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    location_city: '',
    location_state: '',
    location_country: 'Australia',
    citizenship_status: '',
    clearance_level: '',
    notice_period: '',
    payroll_type: '',
    pty_ltd_abn: '',
  });

  const [skills, setSkills] = useState([]);
  const [skillsToAdd, setSkillsToAdd] = useState([]);
  const [skillsToDelete, setSkillsToDelete] = useState([]);

  const [files, setFiles] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);

  // CV skill confirmation after a resume re-upload
  const [showCvSkillsDialog, setShowCvSkillsDialog] = useState(false);
  const [cvSkills, setCvSkills] = useState([]);
  const [cvScanning, setCvScanning] = useState(false);
  const [cvScanError, setCvScanError] = useState(null);
  const [cvExcludedCount, setCvExcludedCount] = useState(0);
  const [savingCvSkills, setSavingCvSkills] = useState(false);
  const [fileToUpload, setFileToUpload] = useState(null);
  const [fileType, setFileType] = useState('cv');
  const [availableSkills, setAvailableSkills] = useState([]);

  useEffect(() => {
    if (candidate && open) {
      setFormData({
        first_name: candidate.first_name || '',
        last_name: candidate.last_name || '',
        phone: candidate.phone || '',
        location_city: candidate.location_city || '',
        location_state: candidate.location_state || '',
        location_country: candidate.location_country || 'Australia',
        citizenship_status: candidate.citizenship_status || '',
        clearance_level: candidate.clearance_level || '',
        notice_period: candidate.notice_period || '',
        payroll_type: candidate.payroll_type || '',
        pty_ltd_abn: candidate.pty_ltd_abn || '',
      });
      setSkills(existingSkills || []);
      setSkillsToAdd([]);
      setSkillsToDelete([]);
      setFiles(existingFiles || []);

      // Fetch available skills
      fetchAvailableSkills();
    }
  }, [candidate, open, existingSkills, existingFiles]);

  const fetchAvailableSkills = async () => {
    const { data, error } = await supabase
      .from('skills_master')
      .select('id, name, category')
      .eq('status', 'active')
      .order('name');

    if (error) {
      console.error('Error fetching skills:', error);
      return;
    }

    setAvailableSkills(data || []);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      // 1. Update candidate details
      const { error: updateError } = await supabase
        .from('candidates')
        .update(formData)
        .eq('id', candidateId);

      if (updateError) throw updateError;

      // 2. Process skill deletions
      if (skillsToDelete.length > 0) {
        const { error: deleteError } = await supabase
          .from('candidate_skills')
          .delete()
          .in('id', skillsToDelete);

        if (deleteError) throw deleteError;
      }

      // 3. Process skill additions
      for (const skillToAdd of skillsToAdd) {
        // If it's a pending skill (custom text entry), create it first
        if (skillToAdd.isPending) {
          // Check if skill already exists
          const { data: existingSkill } = await supabase
            .from('skills_master')
            .select('id, name, status')
            .ilike('name', skillToAdd.name)
            .maybeSingle();

          if (!existingSkill) {
            // Create new pending skill
            const { error: createError } = await supabase
              .from('skills_master')
              .insert({
                name: skillToAdd.name,
                status: 'pending',
                category: 'platform', // Default category for pending skills
              });

            if (createError) {
              console.error('Error creating pending skill:', createError);
              // Continue to next skill
              continue;
            }
          }

          // Add skill to candidate using RPC
          const { error: rpcError } = await supabase.rpc('add_candidate_skill', {
            p_candidate_id: candidateId,
            p_skill_name: skillToAdd.name,
            p_level: skillToAdd.level,
            p_source: 'manual',
          });

          if (rpcError) throw rpcError;
        } else {
          // Existing active skill
          const { error: rpcError } = await supabase.rpc('add_candidate_skill', {
            p_candidate_id: candidateId,
            p_skill_name: skillToAdd.name || skillToAdd.skill?.name,
            p_level: skillToAdd.level,
            p_source: 'manual',
          });

          if (rpcError) throw rpcError;
        }
      }

      setSuccess('Details updated successfully!');
      setTimeout(() => {
        onSuccess();
      }, 1000);
    } catch (err) {
      console.error('Error updating details:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSkillsChange = (newSkills) => {
    // newSkills is an array of { id, name, level, isPending?, source? }
    // We need to compare with existing skills to determine what was added/removed

    const currentSkillIds = skills.map(s => s.id);
    const newSkillIds = newSkills.map(s => s.id);

    // Find removed skills
    const removedSkillIds = currentSkillIds.filter(id => !newSkillIds.includes(id));

    // Find added skills
    const addedSkills = newSkills.filter(s => !currentSkillIds.includes(s.id));

    // Process removals
    removedSkillIds.forEach(skillId => {
      const isNewSkill = skillId.toString().startsWith('temp-') || skillId.toString().startsWith('pending-');

      if (isNewSkill) {
        // Remove from skillsToAdd
        setSkillsToAdd(prev => prev.filter(s => s.id !== skillId));
      } else {
        // Mark existing skill for deletion
        setSkillsToDelete(prev => [...prev, skillId]);
      }
    });

    // Process additions
    addedSkills.forEach(skillToAdd => {
      const tempSkill = {
        id: skillToAdd.id,
        skill: {
          name: skillToAdd.name,
          category: availableSkills.find(s => s.id === skillToAdd.id)?.category || null
        },
        name: skillToAdd.name,
        level: skillToAdd.level,
        isPending: skillToAdd.isPending || false,
        isNew: true
      };

      setSkillsToAdd(prev => [...prev, tempSkill]);
    });

    // Update skills display - convert newSkills format to skills format
    const updatedSkills = newSkills.map(s => {
      // Check if it's an existing skill
      const existing = skills.find(es => es.id === s.id);
      if (existing) {
        return existing;
      }

      // It's a new skill
      return {
        id: s.id,
        skill: {
          name: s.name,
          category: availableSkills.find(as => as.id === s.id)?.category || null
        },
        name: s.name,
        level: s.level,
        isPending: s.isPending || false
      };
    });

    setSkills(updatedSkills);
  };

  const handleFileUpload = async () => {
    if (!fileToUpload) return;
    if (!candidateId) {
      setError('Unable to upload file. Please refresh and try again.');
      return;
    }

    try {
      setUploadingFile(true);
      setError(null);

      const fileName = `profiles/${candidateId}/${Date.now()}_${fileToUpload.name}`;

      const { error: uploadError } = await supabase.storage
        .from('candidate-documents')
        .upload(fileName, fileToUpload);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('candidate-documents')
        .getPublicUrl(fileName);

      const { error: insertError } = await supabase
        .from('candidate_files')
        .insert({
          candidate_id: candidateId,
          file_type: fileType,
          file_name: fileToUpload.name,
          file_url: publicUrl,
          file_size: fileToUpload.size,
        });

      if (insertError) throw insertError;

      // Refetch files
      const { data: updatedFiles, error: fetchError } = await supabase
        .from('candidate_files')
        .select('*')
        .eq('candidate_id', candidateId)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      setFiles(updatedFiles || []);
      setSuccess('File uploaded successfully!');
      setTimeout(() => setSuccess(null), 3000);

      // Re-uploading a CV surfaces any skills not already on the profile.
      // Held-skill exclusion happens server-side, so the candidate is only asked
      // about genuinely new ones.
      if (fileType === 'cv') {
        scanUploadedCv(fileToUpload);
      }

      setFileToUpload(null);
    } catch (err) {
      console.error('Error uploading file:', err);
      setError(err.message);
    } finally {
      setUploadingFile(false);
    }
  };

  const scanUploadedCv = async (file) => {
    setCvScanning(true);
    setCvScanError(null);
    setCvSkills([]);
    setCvExcludedCount(0);
    setShowCvSkillsDialog(true);

    try {
      const { matched, excludedCount } = await scanCvFileForSkills(file, {
        context: 'profile',
      });
      setCvSkills(matched);
      setCvExcludedCount(excludedCount);

      // Nothing new and nothing already held means there is nothing worth showing
      if (matched.length === 0 && excludedCount === 0) {
        setShowCvSkillsDialog(false);
      }
    } catch (err) {
      console.error('CV skill scan failed:', err);
      setCvScanError(err.message || 'Could not read skills from your CV');
    } finally {
      setCvScanning(false);
    }
  };

  const handleConfirmCvSkills = async () => {
    if (cvSkills.length === 0) {
      setShowCvSkillsDialog(false);
      return;
    }

    try {
      setSavingCvSkills(true);
      await saveConfirmedSkills(candidateId, cvSkills);

      // Reflect the new skills in this modal's local list straight away, so the
      // candidate sees them without closing and reopening
      setSkills(prev => ([
        ...prev,
        ...cvSkills.map(s => ({
          id: `cv-${s.skill_id}`,
          skill_id: s.skill_id,
          level: null,
          skills_master: { name: s.name, category: s.category },
        })),
      ]));

      setShowCvSkillsDialog(false);
      setSuccess(`Added ${cvSkills.length} skill${cvSkills.length === 1 ? '' : 's'} to your profile`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving CV skills:', err);
      setCvScanError(err.message || 'Could not save these skills');
    } finally {
      setSavingCvSkills(false);
    }
  };

  const handleDeleteFile = async (fileId) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const API_URL = process.env.REACT_APP_API_URL || 'https://appsytec-backend.vercel.app/api';
      const response = await fetch(`${API_URL}/admin/candidates/files/${fileId}/request-deletion`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to request file deletion');
      }

      setFiles(files.filter(f => f.id !== fileId));
      setSuccess('File marked for deletion.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error requesting file deletion:', err);
      setError(err.message);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0">
        {/* Header - sticky at top */}
        <div className="p-6 pb-0">
        <DialogHeader>
          <DialogTitle>Edit My Details</DialogTitle>
          <DialogDescription>Update your personal and professional information</DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert className="bg-green-50 text-green-900 border-green-200">
            <Check className="h-4 w-4" />
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        )}
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-6">

        <div className="space-y-6">
          {/* Personal Information */}
          <Card>
            <CardHeader>
              <CardTitle>Personal Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_name">First Name</Label>
                  <Input
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                  />
                </div>
                <div>
                  <Label htmlFor="last_name">Last Name</Label>
                  <Input
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="location_city">City</Label>
                  <Input
                    id="location_city"
                    name="location_city"
                    value={formData.location_city}
                    onChange={handleInputChange}
                  />
                </div>
                <div>
                  <Label htmlFor="location_state">State</Label>
                  <select
                    id="location_state"
                    name="location_state"
                    value={formData.location_state}
                    onChange={handleInputChange}
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  >
                    <option value="">Select state</option>
                    {AUSTRALIAN_STATES.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="location_country">Country</Label>
                  <Input
                    id="location_country"
                    name="location_country"
                    value={formData.location_country}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Professional Information */}
          <Card>
            <CardHeader>
              <CardTitle>Professional Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="citizenship_status">Citizenship Status</Label>
                  <Input
                    id="citizenship_status"
                    name="citizenship_status"
                    value={formData.citizenship_status}
                    onChange={handleInputChange}
                    placeholder="e.g., Australian Citizen"
                  />
                </div>
                <div>
                  <Label htmlFor="clearance_level">Security Clearance</Label>
                  <select
                    id="clearance_level"
                    name="clearance_level"
                    value={formData.clearance_level}
                    onChange={handleInputChange}
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  >
                    {CLEARANCE_LEVELS.map(level => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="notice_period">Notice Period</Label>
                  <Input
                    id="notice_period"
                    name="notice_period"
                    value={formData.notice_period}
                    onChange={handleInputChange}
                    placeholder="e.g., 2 weeks"
                  />
                </div>
                <div>
                  <Label htmlFor="payroll_type">Payroll Type</Label>
                  <select
                    id="payroll_type"
                    name="payroll_type"
                    value={formData.payroll_type}
                    onChange={handleInputChange}
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  >
                    <option value="">Select type</option>
                    <option value="PAYG">PAYG</option>
                    <option value="PTY LTD">PTY LTD</option>
                  </select>
                </div>
              </div>

              {formData.payroll_type === 'PTY LTD' && (
                <div>
                  <Label htmlFor="pty_ltd_abn">PTY LTD ABN</Label>
                  <Input
                    id="pty_ltd_abn"
                    name="pty_ltd_abn"
                    value={formData.pty_ltd_abn}
                    onChange={handleInputChange}
                    placeholder="12 345 678 901"
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Skills Management */}
          <Card>
            <CardHeader>
              <CardTitle>Skills</CardTitle>
            </CardHeader>
            <CardContent>
              <SkillsInput
                skills={skills.map(s => ({
                  id: s.id,
                  name: s.skill?.name || s.name || 'Unknown',
                  level: s.level,
                  isPending: s.isPending || false
                }))}
                onChange={handleSkillsChange}
                availableSkills={availableSkills}
                allowFreeText={true}
                placeholder="Type to search or add custom skills..."
              />
            </CardContent>
          </Card>

          {/* File Upload */}
          <Card>
            <CardHeader>
              <CardTitle>Files & Documents</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {files.length > 0 && (
                <div className="space-y-2 mb-4">
                  {files.map((file) => (
                    <div key={file.id} className="flex items-center justify-between p-2 bg-accent rounded-lg">
                      <span className="text-sm">{file.file_name}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteFile(file.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-2">
                <select
                  value={fileType}
                  onChange={(e) => setFileType(e.target.value)}
                  className="flex h-9 w-40 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  {FILE_TYPES.map(type => (
                    <option key={type} value={type}>{FILE_TYPE_LABELS[type] || type}</option>
                  ))}
                </select>
                <Input
                  type="file"
                  onChange={(e) => setFileToUpload(e.target.files[0])}
                  className="flex-1"
                />
                <Button onClick={handleFileUpload} disabled={!fileToUpload || uploadingFile}>
                  {uploadingFile ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Upload'}
                </Button>
              </div>
            </CardContent>
          </Card>
          {/* Social Links */}
          {candidateId && <SocialLinksManager candidateId={candidateId} isAdmin={false} showAddForm={true} />}
        </div>

        </div>

        {/* Footer - sticky at bottom */}
        <div className="border-t p-6 bg-background">
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Save Changes
          </Button>
        </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>

      {/* Skills found in a newly uploaded CV. Sibling of the edit modal rather than
          nested inside it, so the two dialog roots stay independent. */}
      <Dialog open={showCvSkillsDialog} onOpenChange={setShowCvSkillsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Skills from your CV</DialogTitle>
            <DialogDescription>
              We only show skills that aren't already on your profile.
            </DialogDescription>
          </DialogHeader>

          <div className="py-2">
            <CVSkillConfirmation
              variant="inline"
              skills={cvSkills}
              loading={cvScanning}
              error={cvScanError}
              excludedCount={cvExcludedCount}
              onChange={setCvSkills}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCvSkillsDialog(false)}>
              {cvSkills.length > 0 ? 'Not now' : 'Close'}
            </Button>
            {cvSkills.length > 0 && (
              <Button onClick={handleConfirmCvSkills} disabled={savingCvSkills || cvScanning}>
                {savingCvSkills && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Add {cvSkills.length} skill{cvSkills.length === 1 ? '' : 's'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MyDetails;
